import isEqual from "lodash/isEqual";
import BillingModel from "../../../../../common/types/billing-model";
import BillingsTaxiRouteListingFilterSelectOption, {
  BillingsTaxiRouteListingBillingModelFilterSelectOption,
} from "./types/billings-taxi-route-listing-filter-select-option";
import billingsTranslationsHelper from "../../../../../languages/billings-translations.helper";
import BillingsTaxiRouteListingFilterType from "./types/billings-taxi-route-listing-filter-type";
import BillingsTaxiRouteListingFilter, {
  BillingsTaxiRouteListingRouteStartDateFilter,
} from "./types/billings-taxi-route-listing-filter";

const getSearchQueryOptions = (
  query: string
): BillingsTaxiRouteListingFilterSelectOption[] => {
  if (!query) {
    return [];
  }

  const searchFilterTranslations =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().filters
      .search;

  const parseStringToNumber = (string: string): number | undefined => {
    const stringAsNumber = Number(string);

    if (Number.isNaN(stringAsNumber)) {
      return undefined;
    }

    return stringAsNumber;
  };

  const getSearchByRouteIdLabel = (query: string) => {
    return searchFilterTranslations.searchByRouteIdTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const getSearchByDriverLabel = (query: string) => {
    return searchFilterTranslations.searchByDriverTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const getSearchByFleetPartnerLabel = (query: string) => {
    return searchFilterTranslations.searchByFleetPartnerTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const getSearchByOrderInternalIdLabel = (query: string) => {
    return searchFilterTranslations.searchByCargoInternalOrderIdTemplateText.replace(
      `#{query}`,
      query
    );
  };

  const stringOptions = [
    {
      label: getSearchByDriverLabel(query),
      value: {
        type: BillingsTaxiRouteListingFilterType.DRIVER,
        value: query,
      },
    },
    {
      label: getSearchByFleetPartnerLabel(query),
      value: {
        type: BillingsTaxiRouteListingFilterType.FLEET_PARTNER,
        value: query,
      },
    },
  ];

  const options: BillingsTaxiRouteListingFilterSelectOption[] = [
    ...stringOptions,
  ] as BillingsTaxiRouteListingFilterSelectOption[];

  if (parseStringToNumber(query) !== undefined) {
    const numericFilterOptions: BillingsTaxiRouteListingFilterSelectOption[] = [
      {
        label: getSearchByRouteIdLabel(query),
        value: {
          type: BillingsTaxiRouteListingFilterType.ROUTE_ID,
          value: Number(query),
        },
      },
      {
        label: getSearchByOrderInternalIdLabel(query),
        value: {
          type: BillingsTaxiRouteListingFilterType.CARGO_ORDER_INTERNAL_ID,
          value: Number(query),
        },
      },
    ];

    options.push(...numericFilterOptions);
  }

  return options;
};

const getBillingModelOptions = (
  query: string
): BillingsTaxiRouteListingFilterSelectOption[] => {
  const searchFilterTranslations =
    billingsTranslationsHelper.getTaxiRouteBillingsListingTranslations().filters
      .search;

  const allBillingModelSelectOptions: BillingsTaxiRouteListingBillingModelFilterSelectOption[] =
    Object.values(BillingModel).map((billingModel) => {
      const billingModelLabel =
        searchFilterTranslations.searchByBillingModelTemplateText.replace(
          "#{option}",
          billingModel
        );

      return {
        label: billingModelLabel,
        value: {
          type: BillingsTaxiRouteListingFilterType.BILLING_MODEL,
          value: billingModel,
        },
      };
    });

  if (query) {
    return allBillingModelSelectOptions.filter((option) =>
      option.label.toLowerCase().includes(query.toLowerCase())
    );
  }

  return allBillingModelSelectOptions;
};

const getSelectOptionsByQuery = (
  query: string
): BillingsTaxiRouteListingFilterSelectOption[] => {
  const billingModelOptions = getBillingModelOptions(query);
  const queryOptions = getSearchQueryOptions(query);

  return [...billingModelOptions, ...queryOptions];
};

const getSelectOptions = (
  query: string,
  selectedFilters: BillingsTaxiRouteListingFilter[]
): BillingsTaxiRouteListingFilterSelectOption[] => {
  const allFilterOptions = getSelectOptionsByQuery(query);

  return allFilterOptions.filter((option) => {
    return !selectedFilters.find((selectedFilter) =>
      isEqual(selectedFilter.type, option.value.type)
    );
  });
};

const getDefaultFilters = (
  filters: BillingsTaxiRouteListingFilter[]
): BillingsTaxiRouteListingFilter[] => {
  const currentDate = new Date();
  const timezoneOffset = currentDate.getTimezoneOffset();

  const defaultFilters = [
    {
      type: BillingsTaxiRouteListingFilterType.ORDER_START_DATE,
      value: {
        from: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1),
        to: new Date(
          new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + 1,
            0
          ).getTime() -
            timezoneOffset * 60 * 1000
        ),
      },
    } as BillingsTaxiRouteListingRouteStartDateFilter,
  ];

  defaultFilters.forEach((defaultFilter) => {
    const existingFilter = filters.find(
      (filter) => filter.type === defaultFilter.type
    );

    if (existingFilter) {
      return;
    }

    filters.push(defaultFilter);
  });

  return filters;
};

const billingsTaxiRouteListingFilterHelper = {
  getSelectOptions,
  getDefaultFilters,
};

export default billingsTaxiRouteListingFilterHelper;
